import { createSlice } from '@reduxjs/toolkit';
import { contractorApi } from '@store/rtk/contractors';
import { locationType, selectItemType } from '@src/types';
import { TABLE_COLUMNS } from '@uikit/Tables/columns';
import { TableData } from '@uikit/Tables/types';

type ContractorInfoDataType = {
  id?: number;
  name: string;
  shortName: string | null;
  npi: string;
  tin: string;
  status: selectItemType | null;
  activeNetworks: Array<string>;
  contractorType: selectItemType | null;
  atmsSoftware: selectItemType | null;
  timeFrame: selectItemType | null;
  email: string;
  logos: string | null;
  website: string;
  primaryPhone: string;
  primaryPhoneExt: string;
  secondaryPhone: string;
  secondaryPhoneExt: string;
  faxNumber: string;
  physicalAddress: locationType;
  mailingAddress: locationType;
  payors?: Array<selectItemType>;
  dispatchPlatform?: selectItemType | null;
};

type contractorBasicAnalytics = {
  completionRate: number;
  distinctMembers: number;
  driverNoShows: number;
  timeliness: number;
  totalRideCount: number;
  verifiedComplete: number;
};

interface ContractorInfoState {
  contractorInfo: ContractorInfoDataType | null;
  isContractorSaved: boolean;
  contractorBasicAnalytics: contractorBasicAnalytics | null;
  draftFormValues: any | null;
  draftContractorId: string | number | null;
  isDraftEditing: boolean;
  notificationData: TableData;
}

const {
  notificationContactsTableColumns,
  notificationContactsTableFiltrationColumns,
} = TABLE_COLUMNS;

const initialState: ContractorInfoState = {
  contractorInfo: null,
  isContractorSaved: true,
  contractorBasicAnalytics: null,
  draftFormValues: null,
  draftContractorId: null,
  isDraftEditing: false,
  notificationData: {
    columns: notificationContactsTableColumns,
    columnsForFilter: notificationContactsTableFiltrationColumns,
    rows: [],
  },
};

export const contractorInfoSlice = createSlice({
  name: 'contractorInfo',
  initialState,
  reducers: {
    setIsContractorSaved: (state, { payload }) => {
      state.isContractorSaved = payload;
    },
    setContractorInfo: (state, { payload }) => {
      state.contractorInfo = payload;
    },
    setContractorDraft: (state, { payload }) => {
      state.draftFormValues = payload.values;
      state.draftContractorId = payload.contractorId;
      state.isDraftEditing = payload.isEditing;
    },
    clearContractorDraft: (state) => {
      state.draftFormValues = null;
      state.draftContractorId = null;
      state.isDraftEditing = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        contractorApi.endpoints.getContractor.matchFulfilled,
        (state, { payload }) => {
          state.contractorInfo = payload;
        }
      )
      .addMatcher(
        contractorApi.endpoints.getContractorById.matchFulfilled,
        (state, { payload }) => {
          state.contractorInfo = {
            ...payload,
            activeNetworks: [],
            atmsSoftware: null,
            timeFrame: null,
          } as any;
        }
      )
      .addMatcher(
        contractorApi.endpoints.contractorBasicAnalytics.matchFulfilled,
        (state, { payload }) => {
          state.contractorBasicAnalytics = payload;
        }
      )
      .addMatcher(
        contractorApi.endpoints.getContractorNotificationContacts
          .matchFulfilled,
        (state, { payload }) => {
          state.notificationData = {
            ...state.notificationData,
            ...payload,
            rows: (payload?.content ?? []).map((contact) => ({
              ...contact,
              notificationMethod: contact?.notificationMethod?.label,
            })),
            requestTablePageChange:
              contractorApi.endpoints.getContractorNotificationContacts,
          };
        }
      );
  },
});

export const selectContractor = (state: {
  contractorInfo: ContractorInfoState;
}) => state.contractorInfo;
export const {
  setContractorInfo,
  setIsContractorSaved,
  setContractorDraft,
  clearContractorDraft,
} = contractorInfoSlice.actions;
export const contractorInfoReducer = contractorInfoSlice.reducer;
